import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { baseApi } from '@/services/api';

// Feature reducers - Import ALL of them
import authReducer from '@/store/features/auth/authSlice';
import mainReducer from '@/store/features/main/mainSlice';
import eventsReducer from '@/store/features/events/eventsSlice';
import projectsReducer from '@/store/features/projects/projectsSlice';
import bookingsReducer from '@/store/features/bookings/bookingsSlice';
import notificationsReducer from '@/store/features/notifications/notificationsSlice';
import analyticsReducer from '@/store/features/analytics/analyticsSlice';
import collaborationReducer from '@/store/features/collaboration/collaborationSlice';
import deploymentReducer from '@/store/features/deployment/deploymentSlice';
import agentsReducer from '@/store/features/agents/agentsSlice';
import githubReducer from '@/store/features/integrations/github/githubSlice';
import vercelReducer from '@/store/features/integrations/vercel/vercelSlice';
import usersReducer from '@/store/features/users/usersSlice';
import ticketsReducer from '@/store/features/tickets/ticketsSlice';
import venuesReducer from '@/store/features/venues/venuesSlice';
import chatReducer from '@/store/features/chat/chatSlice';
import paymentsReducer from '@/store/features/payments/paymentsSlice';

// Middleware
import { errorLogger } from './middleware/errorLogger';
import { tokenRefreshMiddleware } from './middleware/tokenRefreshMiddleware';
import { webSocketMiddleware } from './middleware/webSocketMiddleware';

export const store = configureStore({
  reducer: {
    // API
    [baseApi.reducerPath]: baseApi.reducer,
    
    // Core Features
    auth: authReducer,
    main: mainReducer,
    events: eventsReducer,
    bookings: bookingsReducer,
    
    // User Management
    users: usersReducer,
    tickets: ticketsReducer,
    venues: venuesReducer,
    
    // AI Platform
    projects: projectsReducer,
    agents: agentsReducer,
    collaboration: collaborationReducer,
    deployment: deploymentReducer,
    
    // Integrations
    github: githubReducer,
    vercel: vercelReducer,
    
    // Infrastructure
    notifications: notificationsReducer,
    analytics: analyticsReducer,

    chat: chatReducer,
    payments: paymentsReducer,
  },
  
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false, // Disable if needed
    })
      .concat(baseApi.middleware)
      .concat(errorLogger)
      .concat(tokenRefreshMiddleware)
      .concat(webSocketMiddleware),
  
  devTools: import.meta.env.DEV,
});

setupListeners(store.dispatch);

// Export types
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export type AppStore = typeof store;
